/*
 * extractAddress returns an address object from purchaseDetails
 *
 * @param {object} purchaseDetails object
 * @returns {object} address object
 */
export function extractAddress(purchaseDetails) {
  return {
    address: purchaseDetails.address,
    address2: purchaseDetails.address2 ?? undefined,
    country: purchaseDetails.country,
    state: purchaseDetails.state,
    city: purchaseDetails.city,
    zipCode: purchaseDetails.zip_code,
    phoneNumber: purchaseDetails.phone_number,
  };
}

/*
 * concatAddress returns a string form of an address from purchaseDetails
 *
 * @param {purchaseDetails} purchaseDetails
 * @returns {string} address string
 */
export function concatAddress(purchaseDetails) {
  return `${purchaseDetails.address}, ${
    purchaseDetails.address2 ? `${purchaseDetails.address2}, ` : ""
  }${purchaseDetails.city}, ${purchaseDetails.state}, ${
    purchaseDetails.country
  } ${purchaseDetails.zip_code}`;
}

/*
 * extractPaymentDetails returns a paymentDetails object from purchaseDetails
 *
 * @param {object} purchaseDetails
 * @returns {object} paymentDetails
 */
export function extractPaymentDetails(purchaseDetails) {
  return {
    paymentMethods: purchaseDetails.stripe?.paymentMethods ?? [],
    defaultSource: purchaseDetails.stripe.defaultSource,
    defaultCard: purchaseDetails.stripe.defaultCard,
  };
}

/*
 * createInitialState returns an initial purchase state from purchaseDetails
 *
 * @param {object} purchaseDetails
 * @returns {object} initial state object
 */
export function createInitialState(purchaseDetails) {
  return {
    savedPaymentDetails: extractPaymentDetails(purchaseDetails),
    billingAddress: extractAddress(purchaseDetails),
    shippingAddress: extractAddress(purchaseDetails),
    defaultAddress: concatAddress(purchaseDetails),
    rememberMe: true,
    useSavedCard: Boolean(purchaseDetails.stripe?.defaultSource),
  };
}

/*
 * createPayload returns an address payload
 *
 * @param {object} initialization object { showShippingAddress, showBillingAddress, state }
 * @returns {object} payload
 */
export function createPayload({
  showBillingAddress,
  showShippingAddress,
  state,
}) {
  return {
    shippingAddress: showShippingAddress ? state.shippingAddress : void 0,
    billingAddress: showBillingAddress ? state.billingAddress : void 0,
    rememberMe: state.rememberMe,
  };
}

export function getActionForState({ state, stripe }) {
  if (state.useSavedCard) {
    return stripe.confirmCardPayment;
  }
  return stripe.confirmPayment;
}

/*
 * composeStripeInit returns the initialization object for stripe action based
 * upon state.useSavedCard
 *
 * @param {object} initialization object { purchaseDetails, elements, state }
 * @returns {object|string} stripe init
 */
export function composeStripeInitForState({
  purchaseDetails,
  elements,
  state,
}) {
  if (state.useSavedCard) {
    return purchaseDetails?.stripe?.clientSecret;
  }

  return {
    elements,
    redirect: "if_required",
    confirmParams: {
      return_url: `${window.location}`,
    },
  };
}

export function genericReducer(state, action) {
  return { ...state, [action.type]: action.payload };
}

export function labelForPaymentDetails({ defaultSource }) {
  return defaultSource ? "Other Payment Method" : "New Payment Method";
}
